/**
 * Корзина сотрудника: серверная (живёт между сессиями и устройствами), своя у
 * каждого. Список — тот же `ReferenceBrowser`, но в режиме `basket` (в панели
 * «Убрать из корзины»); здесь только то, что относится к корзине целиком:
 * привязать всё к проекту и очистить.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronDown } from "lucide-react";
import { useRef, useState } from "react";
import { Link } from "react-router";

import { LibNav } from "@/components/library/LibNav";
import { ReferenceBrowser } from "@/components/library/ReferenceBrowser";
import { usePopoverClose } from "@/components/trends/TagPicker";
import { btn, btnPrimary, ErrorLine, input, Loading } from "@/components/trends/ui";
import { apiFetch } from "@/lib/api";
import { attachToProject, basketClear, BASKET_MAX, libraryKeys, loadBasket, type BasketView } from "@/lib/library";
import { num } from "@/lib/media";
import { qk, type ProjectRow } from "@/lib/project";
import { retryQuery } from "@/lib/trends";

type Attached = { pid: number; attached: number; already: number };

function AttachPopover({ count, onClose }: { count: number; onClose: () => void }) {
  const box = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();
  const [pid, setPid] = useState("");
  usePopoverClose(box, onClose);

  const projects = useQuery({
    queryKey: qk.projects,
    queryFn: () => apiFetch<ProjectRow[]>("/api/projects"),
    retry: retryQuery,
  });
  const attach = useMutation({
    mutationFn: async (id: number): Promise<Attached> => {
      const ids = queryClient.getQueryData<BasketView>(libraryKeys.basket)?.items.map((i) => i.id) ?? [];
      const got = await attachToProject(id, ids);
      return { pid: id, attached: got.attached, already: got.already };
    },
    onSuccess: ({ pid: id }) => void queryClient.invalidateQueries({ queryKey: qk.project(id) }),
  });

  const chosen = Number(pid);
  return (
    <div
      ref={box}
      className="absolute right-0 top-full z-10 mt-2 grid w-[320px] gap-2 rounded-[var(--radius-card)] border border-border bg-bg p-3 shadow-xl"
    >
      {projects.isPending ? (
        <Loading />
      ) : projects.isError ? (
        <ErrorLine error={projects.error} />
      ) : projects.data.length === 0 ? (
        <p className="text-[12.5px] text-fg-muted">Проектов пока нет — создайте проект.</p>
      ) : (
        <>
          <select
            className={input}
            aria-label="Проект"
            value={pid}
            onChange={(e) => {
              setPid(e.target.value);
              attach.reset();
            }}
          >
            <option value="">выберите проект</option>
            {projects.data.map((p) => (
              <option key={p.id} value={p.id}>
                {p.project_name}
              </option>
            ))}
          </select>
          <button
            type="button"
            className={btnPrimary}
            disabled={!chosen || attach.isPending}
            onClick={() => attach.mutate(chosen)}
          >
            Привязать {num(count)}
          </button>
        </>
      )}
      {attach.isError ? <ErrorLine error={attach.error} /> : null}
      {attach.data ? (
        <p className="text-[12.5px] text-ok" role="status">
          привязано {num(attach.data.attached)}, уже было {num(attach.data.already)}{" "}
          <Link className="underline" to={`/project/${attach.data.pid}`}>
            открыть проект
          </Link>
        </p>
      ) : null}
    </div>
  );
}

export function BasketPage() {
  const queryClient = useQueryClient();
  const [attachOpen, setAttachOpen] = useState(false);
  const data = useQuery({ queryKey: libraryKeys.basket, queryFn: loadBasket, retry: retryQuery });
  const clear = useMutation({
    mutationFn: basketClear,
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: libraryKeys.basket }),
  });

  const count = data.data?.items.length ?? 0;
  const onClear = () => {
    if (window.confirm(`Очистить корзину? ${num(count)} референсов`)) clear.mutate();
  };

  return (
    <div className="grid gap-3 p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-[18px] font-semibold tracking-[-0.01em] text-fg">Корзина</h1>
          <p className="mt-0.5 text-[12.5px] text-fg-muted tnum">
            {num(count)} из {num(data.data?.max ?? BASKET_MAX)} · своя у каждого сотрудника, живёт на сервере
          </p>
        </div>
        <div className="relative flex items-center gap-2">
          <button
            type="button"
            className={btn}
            disabled={count === 0}
            aria-expanded={attachOpen}
            onClick={() => setAttachOpen(!attachOpen)}
          >
            Привязать к проекту <ChevronDown size={14} />
          </button>
          {attachOpen ? <AttachPopover count={count} onClose={() => setAttachOpen(false)} /> : null}
          <button type="button" className={btn} disabled={count === 0 || clear.isPending} onClick={onClear}>
            Очистить
          </button>
        </div>
      </div>

      <LibNav current="basket" />
      {clear.isError ? <ErrorLine error={clear.error} /> : null}

      {data.isPending ? (
        <Loading />
      ) : data.isError && !data.data ? (
        <ErrorLine error={data.error} />
      ) : (
        <ReferenceBrowser
          items={data.data?.items ?? []}
          mode="basket"
          queryKey={libraryKeys.basket}
          emptyText="Корзина пуста — отметьте референсы в библиотеке и нажмите «В корзину»"
        />
      )}
    </div>
  );
}
